import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Undo as RefundIcon,
  // LockOpen as ReleaseIcon, // ESCROW: Hidden for now, will be used later
} from '@mui/icons-material';
import apiService from '../../services/api';
import { Payment, PaymentFilterParams } from '../../types';

const TransactionMonitoringPage: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState<PaymentFilterParams>({
    per_page: 20,
    page: 1,
  });
  const [pagination, setPagination] = useState<any>(null);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundReason, setRefundReason] = useState('');

  const fetchPayments = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');

      const response = await apiService.getPayments(filters);
      setPayments(response.data);
      setPagination(response.pagination);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load payments');
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleFilterChange = (key: keyof PaymentFilterParams, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleViewDetails = (payment: Payment) => {
    setSelectedPayment(payment);
    setDetailDialogOpen(true);
  };

  const handleRefund = async () => {
    if (!selectedPayment) return;

    try {
      await apiService.refundPayment(selectedPayment.id, refundReason);
      setRefundDialogOpen(false);
      setRefundReason('');
      fetchPayments();
      alert('Refund processed successfully');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to process refund');
    }
  };

  // ESCROW: Hidden for now, will be used later
  // const handleReleaseEscrow = async (paymentId: number) => {
  //   if (!window.confirm('Are you sure you want to release this payment from escrow?')) {
  //     return;
  //   }

  //   try {
  //     await apiService.releaseEscrow(paymentId);
  //     fetchPayments();
  //     alert('Escrow released successfully');
  //   } catch (err: any) {
  //     alert(err.response?.data?.message || 'Failed to release escrow');
  //   }
  // };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
      case 'processing':
        return 'warning';
      case 'failed':
      case 'cancelled':
        return 'error';
      case 'refunded':
        return 'info';
      default:
        return 'default';
    }
  };

  // ESCROW: Hidden for now, will be used later
  // const getEscrowStatusColor = (status: string) => {
  //   switch (status) {
  //     case 'held':
  //       return 'warning';
  //     case 'released':
  //       return 'success';
  //     case 'refunded':
  //       return 'error';
  //     default:
  //       return 'default';
  //   }
  // };

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
        Transaction Monitoring
      </Typography>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <TextField
              label="Search"
              size="small"
              placeholder="Search payments..."
              sx={{ minWidth: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status || ''}
                label="Status"
                onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="refunded">Refunded</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
            {/* ESCROW: Hidden for now, will be used later */}
            {/* <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Escrow Status</InputLabel>
              <Select
                value={filters.escrow_status || ''}
                label="Escrow Status"
                onChange={(e) => handleFilterChange('escrow_status', e.target.value || undefined)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="held">Held</MenuItem>
                <MenuItem value="released">Released</MenuItem>
                <MenuItem value="refunded">Refunded</MenuItem>
              </Select>
            </FormControl> */}
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchPayments}
            >
              Refresh
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Reference</TableCell>
                  <TableCell>Patient</TableCell>
                  <TableCell>Professional</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Commission</TableCell>
                  <TableCell>Status</TableCell>
                  {/* ESCROW: Hidden for now, will be used later */}
                  {/* <TableCell>Escrow</TableCell> */}
                  <TableCell>Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell>{payment.payment_reference}</TableCell>
                    <TableCell>{payment.patient?.name || 'N/A'}</TableCell>
                    <TableCell>{payment.professional?.name || 'N/A'}</TableCell>
                    <TableCell>₹{parseFloat(payment.amount.toString()).toFixed(2)}</TableCell>
                    <TableCell>₹{parseFloat(payment.commission_amount.toString()).toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip
                        label={payment.status}
                        color={getStatusColor(payment.status) as any}
                        size="small"
                      />
                    </TableCell>
                    {/* ESCROW: Hidden for now, will be used later */}
                    {/* <TableCell>
                      <Chip
                        label={payment.escrow_status}
                        color={getEscrowStatusColor(payment.escrow_status) as any}
                        size="small"
                      />
                    </TableCell> */}
                    <TableCell>
                      {new Date(payment.created_at).toLocaleDateString('en-IN')}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(payment)}
                        color="primary"
                      >
                        <ViewIcon />
                      </IconButton>
                      {/* ESCROW: Hidden for now, will be used later */}
                      {/* {payment.status === 'completed' && payment.escrow_status === 'held' && (
                        <IconButton
                          size="small"
                          onClick={() => handleReleaseEscrow(payment.id)}
                          color="success"
                        >
                          <ReleaseIcon />
                        </IconButton>
                      )} */}
                      {payment.status === 'completed' && (
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedPayment(payment);
                            setRefundDialogOpen(true);
                          }}
                          color="error"
                        >
                          <RefundIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {pagination && (
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
              <Typography variant="body2">
                Showing {pagination.from} to {pagination.to} of {pagination.total} payments
              </Typography>
              <Box display="flex" gap={1}>
                <Button
                  disabled={pagination.current_page === 1}
                  onClick={() => handleFilterChange('page', pagination.current_page - 1)}
                >
                  Previous
                </Button>
                <Button
                  disabled={pagination.current_page === pagination.last_page}
                  onClick={() => handleFilterChange('page', pagination.current_page + 1)}
                >
                  Next
                </Button>
              </Box>
            </Box>
          )}
        </>
      )}

      {/* Payment Details Dialog */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Payment Details</DialogTitle>
        <DialogContent>
          {selectedPayment && (
            <Box>
              <Typography><strong>Reference:</strong> {selectedPayment.payment_reference}</Typography>
              <Typography><strong>Amount:</strong> ₹{parseFloat(selectedPayment.amount.toString()).toFixed(2)}</Typography>
              <Typography><strong>Commission:</strong> ₹{parseFloat(selectedPayment.commission_amount.toString()).toFixed(2)}</Typography>
              <Typography><strong>Platform Fee:</strong> ₹{parseFloat(selectedPayment.platform_fee.toString()).toFixed(2)}</Typography>
              <Typography><strong>Net Amount:</strong> ₹{parseFloat(selectedPayment.net_amount.toString()).toFixed(2)}</Typography>
              <Typography><strong>Status:</strong> {selectedPayment.status}</Typography>
              {/* ESCROW: Hidden for now, will be used later */}
              {/* <Typography><strong>Escrow Status:</strong> {selectedPayment.escrow_status}</Typography> */}
              <Typography><strong>Payment Method:</strong> {selectedPayment.payment_method}</Typography>
              {selectedPayment.gateway_payment_id && (
                <Typography><strong>Gateway ID:</strong> {selectedPayment.gateway_payment_id}</Typography>
              )}
              <Typography><strong>Created:</strong> {new Date(selectedPayment.created_at).toLocaleString('en-IN')}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog open={refundDialogOpen} onClose={() => setRefundDialogOpen(false)}>
        <DialogTitle>Process Refund</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Refund Reason (Optional)"
            multiline
            rows={3}
            value={refundReason}
            onChange={(e) => setRefundReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRefund} color="error" variant="contained">
            Process Refund
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TransactionMonitoringPage;

